import { type IFavoriteProfessional } from '~/domain/FavoriteProfessionals';
import { type IRecentProfessional } from '~/domain/RecentProfessionals';
import { getRecentProfessionalId } from '~/domain/RecentProfessionals/recentProfessionalsHelpers';
import { type IRecommendedProfessional } from '~/domain/RecommendedProfessionals';
import { type IBadge } from '~/domain/RecommendedProfessionals/recommendedProfessionalsTypes';
import { type TEducationMap } from '~/domain/FavoriteProfessionals/favoriteProfessionalsTypes';
import { UserProfession } from '~/enums/UserProfession';

export type TProCategory = 'favorite' | 'recent' | 'new';

export interface IListItemData {
  id: string;
  fullName: string;
  isAvailable: boolean;
  isFavorite: boolean;
  profileImage: string;
  profession: string;
  professions: string[];
  rating?: {
    averageRating: number;
    professionalSkills?: number;
    interpersonalSkills?: number;
    timeManagement?: number;
  };
  badges: IBadge[];
  education: TEducationMap;
  category: TProCategory;
  professionCode: string;
}

type TProfessionLookup = (code: string) => string;

const resolveProfessionName = (
  professions: string[] | undefined,
  selectedProfession: string | undefined,
  fallback: string | undefined,
  getProfessionName: TProfessionLookup,
): { name: string; code: string } => {
  if (selectedProfession && professions?.includes(selectedProfession)) {
    return { name: getProfessionName(selectedProfession), code: selectedProfession };
  }
  const code = professions?.[0] ?? fallback ?? '';
  if (!code) return { name: '', code: '' };
  return { name: getProfessionName(code), code };
};

export const fromFavorite = (
  pro: IFavoriteProfessional,
  selectedProfession: string | undefined,
  getProfessionName: TProfessionLookup,
): IListItemData => {
  const { name, code } = resolveProfessionName(
    pro.professions,
    selectedProfession,
    undefined,
    getProfessionName,
  );
  return {
    id: pro.id,
    fullName: pro.fullName,
    isAvailable: pro.isAvailable ?? true,
    isFavorite: true,
    profileImage: pro.profileImage,
    profession: name,
    professions: pro.professions ?? [],
    rating: pro.averageProfessionalRating
      ? {
          averageRating: pro.averageProfessionalRating.averageRating,
          professionalSkills: pro.averageProfessionalRating.averageProfessionalSkills,
          interpersonalSkills: pro.averageProfessionalRating.averageInterpersonalSkills,
          timeManagement: pro.averageProfessionalRating.averageTimeManagement,
        }
      : undefined,
    badges: pro.badges ?? [],
    education: pro.education ?? {},
    category: 'favorite',
    professionCode: code,
  };
};

export const fromRecent = (
  pro: IRecentProfessional,
  selectedProfession: string | undefined,
  getProfessionName: TProfessionLookup,
): IListItemData => {
  const { name, code } = resolveProfessionName(
    pro.professional?.professions,
    selectedProfession,
    pro.profession,
    getProfessionName,
  );
  const rating = pro.professional?.overallProfessionalRating;
  return {
    id: getRecentProfessionalId(pro),
    fullName: pro.professional?.fullName ?? '',
    isAvailable: pro.isAvailable ?? true,
    isFavorite: false,
    profileImage: pro.professional?.image ?? '',
    profession: name || UserProfession.getName(pro.profession) || '',
    professions: pro.professional?.professions ?? [],
    rating: rating
      ? {
          averageRating: rating.averageRating,
          professionalSkills: rating.averageProfessionalSkills,
          interpersonalSkills: rating.averageInterpersonalSkills,
          timeManagement: rating.averageTimeManagement,
        }
      : undefined,
    badges: pro.professional?.badges ?? [],
    education: pro.professional?.education ?? {},
    category: 'recent',
    professionCode: code || pro.profession || '',
  };
};

export const fromRecommended = (
  pro: IRecommendedProfessional,
  selectedProfession: string | undefined,
  getProfessionName: TProfessionLookup,
): IListItemData => {
  const { name, code } = resolveProfessionName(
    pro.professions,
    selectedProfession,
    undefined,
    getProfessionName,
  );
  return {
    id: pro.id,
    fullName: pro.fullName,
    isAvailable: pro.isAvailable ?? true,
    isFavorite: false,
    profileImage: pro.profileImage,
    profession: name,
    professions: pro.professions ?? [],
    rating:
      pro.rating != null
        ? {
            averageRating: pro.rating,
            professionalSkills: pro.averageProfessionalSkills,
            interpersonalSkills: pro.averageInterpersonalSkills,
            timeManagement: pro.averageTimeManagement,
          }
        : undefined,
    badges: pro.badges ?? [],
    education: pro.education ?? {},
    category: 'new',
    professionCode: code,
  };
};
